<script setup lang="ts">
/**
 * Le solde de SMS de l'entreprise (écran 9, S:5833-5835), en lecture.
 *
 * Selon le geste `sms_voir_solde` de la matrice :
 *  • absent : rien ;
 *  • inactif (le Basique) : le titre, l'étiquette « Avancé » et la raison ;
 *  • actif : le solde servi, ou « Solde non disponible » quand il est
 *    inconnu, jamais un 0 supposé ; « Solde épuisé » et sa phrase quand le
 *    service le dit ; « Votre offre ne comprend pas les SMS » quand le module
 *    n'est pas souscrit.
 * Aucun bouton : la recharge en libre-service n'existe pas encore (P23).
 */
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import type { Acces } from "@modules/alarmes-v2/acces/acces"
import type { StatutSms } from "@modules/alarmes-v2/stores/sms.store"
import type { AlarmesV2Sms } from "@modules/alarmes-v2/types/alarmes-v2"

const props = defineProps<{
    /** Le verdict de `sms_voir_solde`. */
    acces: Acces
    /** Le solde lu pour la session, `null` s'il n'est pas lu ou illisible. */
    sms: AlarmesV2Sms | null
    /** Où en est la lecture. */
    statut: StatutSms
}>()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()

/** Ce que le bloc montre. */
const getVue = computed(
    ():
        | "chargement"
        | "non_disponible"
        | "non_souscrit"
        | "epuise"
        | "solde" => {
        if (props.statut === "chargement" || props.statut === "attente")
            return "chargement"
        const sms = props.sms
        if (!sms) return "non_disponible"
        if (sms.raison_indisponible === "module_sms_non_souscrit")
            return "non_souscrit"
        if (sms.solde_segments === null) return "non_disponible"
        if (sms.raison_indisponible === "solde_sms_epuise") return "epuise"
        return "solde"
    }
)

/** Le solde servi, en chiffres de France. */
const getSolde = computed((): string =>
    typeof props.sms?.solde_segments === "number"
        ? props.sms.solde_segments.toLocaleString("fr-FR")
        : ""
)
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <section
        v-if="acces.affichage !== 'absent'"
        class="rounded-lg border border-surface-200 bg-surface-0 px-4 py-3"
        data-test="solde-sms"
    >
        <h2 class="text-base font-semibold text-surface-900">
            <LigneNiveau
                v-if="!acces.ok"
                :texte="t('alarmesV2.option.solde.titre')"
                :avance="acces.etiquetteAvance"
                :raison="acces.raison"
            />
            <template v-else>{{ t("alarmesV2.option.solde.titre") }}</template>
        </h2>
        <template v-if="acces.ok">
            <p
                v-if="getVue === 'chargement'"
                class="mt-2 text-sm text-surface-600"
            >
                {{ t("alarmesV2.option.solde.chargement") }}
            </p>
            <p
                v-else-if="getVue === 'non_disponible'"
                class="mt-2 text-sm text-surface-700"
                data-test="solde-non-disponible"
            >
                {{ t("alarmesV2.option.solde.nonDisponible") }}
            </p>
            <p
                v-else-if="getVue === 'non_souscrit'"
                class="mt-2 text-sm text-surface-700"
                data-test="solde-non-souscrit"
            >
                {{ t("alarmesV2.option.solde.nonSouscrit") }}
            </p>
            <template v-else>
                <p class="mt-2 text-2xl font-semibold text-surface-900">
                    <span data-test="solde">{{ getSolde }}</span>
                    <span class="ml-1 text-sm font-medium text-surface-600">{{
                        t("alarmesV2.option.solde.restants")
                    }}</span>
                </p>
                <div
                    v-if="getVue === 'epuise'"
                    class="mt-2 rounded border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-900"
                    data-test="solde-epuise"
                >
                    <strong class="block">{{
                        t("alarmesV2.option.solde.epuiseTitre")
                    }}</strong>
                    {{ t("alarmesV2.option.solde.epuiseTexte") }}
                </div>
            </template>
        </template>
    </section>
</template>
